<template>
  <div class="student-docs">
    <h3 class="mt-40 text-rothek">Мои документы</h3>

    <div v-if="documents === null" class="mt-20 ml-10 student-docs__muted">
      Загрузка документов…
    </div>

    <div v-else-if="!documents.length" class="mt-20 ml-10 student-docs__muted">
      Пока для вас нет документов
    </div>

    <el-row v-else :gutter="17">
      <el-col
        v-for="doc in documents"
        :key="doc.id"
        class="mt-21"
        :lg="8" :md="12" :sm="24" :xs="24"
      >
        <div class="student-docs__card">
          <div class="student-docs__title">{{ doc.title }}</div>

          <div v-if="doc.description" class="student-docs__desc">{{ doc.description }}</div>

          <div v-if="doc.deadline" class="student-docs__deadline">
            Дедлайн загрузки: <b>{{ fmtDay(doc.deadline) }}</b>
            <span v-if="doc.isOverdue" class="student-docs__overdue"> · просрочено</span>
          </div>

          <!-- Шаблон от администратора -->
          <div class="student-docs__row">
            <el-button
              v-if="doc.hasTemplate"
              size="small"
              icon="el-icon-download"
              :loading="downloadingId === ('t' + doc.id)"
              @click="download(doc.templateDownloadPath, doc.templateName, 't' + doc.id)"
            >
              Скачать шаблон
            </el-button>
            <span v-else class="student-docs__muted">Шаблон не приложен</span>
          </div>

          <!-- Подписанная копия слушателя -->
          <div class="student-docs__submission">
            <template v-if="doc.hasSubmission">
              <div class="student-docs__done">
                <i class="el-icon-circle-check"></i>
                Загружено: {{ doc.submissionName }}
              </div>
              <div v-if="doc.submittedAt" class="student-docs__muted student-docs__date">
                {{ fmtDateTime(doc.submittedAt) }}
              </div>
              <div class="student-docs__row">
                <el-button
                  size="small"
                  type="text"
                  icon="el-icon-download"
                  :loading="downloadingId === ('s' + doc.id)"
                  @click="download(doc.submissionDownloadPath, doc.submissionName, 's' + doc.id)"
                >
                  Скачать мою копию
                </el-button>
                <file-upload
                  :action-for-upload="uploadUrl(doc)"
                  :accept="acceptTypes"
                  @input="onUploaded($event, doc)"
                >
                  <el-button size="small" type="text" icon="el-icon-refresh">Заменить файл</el-button>
                </file-upload>
              </div>
            </template>

            <template v-else>
              <file-upload
                :action-for-upload="uploadUrl(doc)"
                :accept="acceptTypes"
                @input="onUploaded($event, doc)"
              >
                <el-button size="small" type="primary" icon="el-icon-upload2">Загрузить файл</el-button>
              </file-upload>
              <div class="student-docs__muted student-docs__hint">Word или PDF (.pdf, .doc, .docx)</div>
            </template>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import FileUpload from "../FileUpload.vue";

export default {
  name: "StudentDocuments",
  components: { FileUpload },
  data() {
    return {
      documents: null,
      downloadingId: null,
      acceptTypes: ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.axios.get("student_documents/my").then((res) => {
        this.documents = Array.isArray(res.data) ? res.data : [];
      }).catch(() => {
        this.documents = [];
      });
    },
    uploadUrl(doc) {
      return this.axios.defaults.baseURL + "student_documents/" + doc.id + "/submission";
    },
    onUploaded(payload, doc) {
      // FileUpload эмитит тело ответа. Успех — если бэкенд вернул отметку о загрузке.
      if (payload && (payload.submittedAt || payload.hasSubmission)) {
        this.$message.success("Файл «" + (doc ? doc.title : "") + "» загружен");
      } else {
        const msg = payload && (payload.detail || payload["hydra:description"] || payload.message);
        this.$message.error(msg || "Не удалось загрузить файл. Допустимы только Word и PDF.");
      }
      this.load();
    },
    async download(path, filename, key) {
      if (!path) return;
      this.downloadingId = key;
      try {
        const res = await this.axios.get(path, {
          responseType: "blob",
          headers: { Authorization: "Bearer " + this.$auth.token() },
        });
        if (!res || (res.status && res.status >= 400)) {
          this.$message.error("Не удалось скачать файл");
          return;
        }
        const blob = new Blob([res.data], { type: res.data.type || "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "document";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$message.error("Не удалось скачать файл");
      } finally {
        this.downloadingId = null;
      }
    },
    fmtDay(v) {
      if (!v) return "";
      const parts = String(v).split("T")[0].split("-");
      return parts.length === 3 ? parts[2] + "." + parts[1] + "." + parts[0] : v;
    },
    fmtDateTime(v) {
      if (!v) return "";
      const d = new Date(v);
      if (isNaN(d.getTime())) return this.fmtDay(v);
      const p = (n) => String(n).padStart(2, "0");
      return p(d.getDate()) + "." + p(d.getMonth() + 1) + "." + d.getFullYear() + " " + p(d.getHours()) + ":" + p(d.getMinutes());
    },
  },
};
</script>

<style lang="scss" scoped>
.student-docs {
  &__muted {
    color: #9aa0a6;
    font-size: 14px;
  }
  &__card {
    height: 100%;
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 14px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    transition: box-shadow 0.2s ease;
    &:hover {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.07);
    }
  }
  &__title {
    font-weight: 700;
    font-size: 17px;
    line-height: 1.3;
    margin-bottom: 8px;
  }
  &__desc {
    color: #5f6368;
    font-size: 14px;
    white-space: pre-line;
    margin-bottom: 12px;
  }
  &__deadline {
    font-size: 14px;
    margin-bottom: 14px;
  }
  &__overdue {
    color: #e64b3b;
    font-weight: 600;
  }
  &__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: auto;
  }
  &__submission {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px dashed #ececec;
  }
  &__done {
    color: #18a689;
    font-weight: 600;
    font-size: 14px;
    word-break: break-word;
  }
  &__date {
    margin: 2px 0 8px;
    font-size: 13px;
  }
  &__hint {
    margin-top: 6px;
    font-size: 12px;
  }
}
</style>
